'use client';

import type { Dispatch } from 'react';
import type { HingeSide } from '../drawing/door';
import type { DoorQuote } from '../data/doorPricing';
import { formatGbp } from '../data/doorPricing';
import { PIECE_LABEL, SIZE_LIMITS, sizeIssues, type Piece, type PieceKind } from './drilling';
import { NumberField } from './NumberField';
import type { DoorOrderAction } from './orderState';

const SIDES: Array<{ id: HingeSide; label: string }> = [
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
  { id: 'top', label: 'Top' },
  { id: 'bottom', label: 'Bottom' },
  { id: 'none', label: 'None' },
];

const KINDS: PieceKind[] = ['door', 'drawer', 'panel'];

/**
 * Step 3: every piece in the order, one row each. Selecting a row (click or
 * focus) makes it the piece the viewer and the drilling editor show.
 */
export function PiecesTable({
  pieces,
  active,
  quote,
  dispatch,
}: {
  pieces: Piece[];
  active: number;
  quote: DoorQuote | null;
  dispatch: Dispatch<DoorOrderAction>;
}) {
  const { minH, maxH, minW, maxW } = SIZE_LIMITS;
  return (
    <>
      <table className="sk-db__table">
        <caption className="sk-visually-hidden">Your pieces. Select a row to see and edit its drilling.</caption>
        <thead>
          <tr>
            <th scope="col">
              <span className="sk-visually-hidden">Piece</span>
            </th>
            <th scope="col">Item</th>
            <th scope="col">Height</th>
            <th scope="col">Width</th>
            <th scope="col">Hinges</th>
            <th scope="col">Qty</th>
            <th scope="col" style={{ textAlign: 'right' }}>
              Price
            </th>
            <th scope="col">
              <span className="sk-visually-hidden">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {pieces.map((piece, i) => {
            const n = i + 1;
            const line = quote?.lines[i];
            const invalid = sizeIssues(piece);
            const select = () => {
              if (i !== active) dispatch({ type: 'select', index: i });
            };
            return (
              <tr key={i} data-active={i === active ? '' : undefined} onClick={select} onFocus={select}>
                <td data-cell="num">
                  <button type="button" className="sk-db__num" aria-pressed={i === active} aria-label={`Piece ${n}: show its drilling`}>
                    {n}
                  </button>
                </td>
                <td data-cell="item" data-label="Item">
                  <select
                    className="sk-db__input"
                    aria-label={`Piece ${n} item`}
                    value={piece.kind}
                    onChange={(e) => dispatch({ type: 'kind', index: i, kind: e.target.value as PieceKind })}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {PIECE_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </td>
                <td data-cell="h" data-label="Height">
                  <NumberField
                    value={piece.h}
                    min={minH}
                    max={maxH}
                    suffix="mm"
                    aria-label={`Piece ${n} height in millimetres`}
                    aria-invalid={!(piece.h >= minH && piece.h <= maxH)}
                    onCommit={(value) => dispatch({ type: 'size', index: i, field: 'h', value })}
                  />
                </td>
                <td data-cell="w" data-label="Width">
                  <NumberField
                    value={piece.w}
                    min={minW}
                    max={maxW}
                    suffix="mm"
                    aria-label={`Piece ${n} width in millimetres`}
                    aria-invalid={!(piece.w >= minW && piece.w <= maxW)}
                    onCommit={(value) => dispatch({ type: 'size', index: i, field: 'w', value })}
                  />
                </td>
                <td data-cell="side" data-label="Hinges">
                  <select
                    className="sk-db__input"
                    aria-label={`Piece ${n} hinge side`}
                    value={piece.hinge.side}
                    onChange={(e) => dispatch({ type: 'hingeSide', index: i, side: e.target.value as HingeSide })}
                  >
                    {SIDES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td data-cell="qty" data-label="Qty">
                  <NumberField
                    value={piece.qty}
                    min={1}
                    className="sk-db__input sk-db__qtyinput"
                    aria-label={`Piece ${n} quantity`}
                    onCommit={(value) => dispatch({ type: 'qty', index: i, value })}
                  />
                </td>
                <td data-cell="price" data-label="Price" className="sk-db__price">
                  {invalid.length ? (
                    <small>{invalid[0]}</small>
                  ) : line ? (
                    <>
                      {formatGbp(line.total)}
                      {line.pricedAs && <small>{`priced as ${line.pricedAs.h}×${line.pricedAs.w}`}</small>}
                    </>
                  ) : (
                    '…'
                  )}
                </td>
                <td data-cell="del">
                  <button
                    type="button"
                    className="sk-db__del"
                    disabled={pieces.length <= 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'remove', index: i });
                    }}
                  >
                    <span aria-hidden="true">×</span>
                    <span className="sk-visually-hidden">{`Remove piece ${n}`}</span>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="sk-db__add" onClick={() => dispatch({ type: 'add' })}>
        + Add another size
      </button>
      <p className="sk-db__hint">
        {`Min ${minH} × ${minW}mm · Max ${maxH} × ${maxW}mm · Measure the door, not the opening. Select a row to check and edit its drilling.`}
      </p>
    </>
  );
}
